<template>
  <vs-popup
    button-close-hidden
    class="popup"
    v-bind="$attrs"
    v-on="$listeners"
  >
    <div class="flex w-full mb-8">
      <h1 class="text-xl font-semibold flex justify-start">
        Send Fuel Supply Email
      </h1>
      <img
        alt="close"
        class="vs-popup--close-button"
        :src="require('@/assets/shipo-popup-icon-close.svg')"
        @click="$emit('close')"
      >
    </div>
    <div class="flex w-full gap-4 ">
      <div class="flex flex-col gap-2 w-full">
        <h2 class="font-semibold text-md flex justify-start">
          Report Selection
        </h2>
        <div class="flex items-center">
          <vs-checkbox
            :disabled="isSendEmailLoading"
            color="primary"
            v-model="fuelSupplyType"
          />
          <p class="text-md flex text-black justify-start">
            Fuel Supply Report
          </p>
        </div>
        <div class="flex items-center">
          <vs-checkbox
            :disabled="isSendEmailLoading"
            color="primary"
            v-model="dailyBunkerType"
          />
          <p class="text-md flex text-black justify-start">
            Daily Bunker Report
          </p>
        </div>
      </div>
      <div class="flex w-full flex-col gap-2 justify-start w-full">
        <h2 class="font-semibold text-md flex justify-start">
          With Attachments?
        </h2>
        <vs-checkbox
          :disabled="isSendEmailLoading"
          class="justify-start"
          color="primary"
          v-model="fuelSupplyAttachment"
        />
        <vs-checkbox
          :disabled="isSendEmailLoading"
          class="justify-start"
          color="primary"
          v-model="dailyBunkerAttachment"
        />
      </div>
    </div>
    <div class="w-full flex flex-col gap-2 my-4">
      <h2 class="font-semibold text-md flex justify-start">
        Recipients
      </h2>
      <vs-textarea
        :disabled="isSendEmailLoading"
        v-model="emails"
        placeholder="Type email addresses, separated with commas"
        height="150px"
        @keydown.space.prevent="insertComma"
      />
    </div>
    <div :class="`mt-5 w-full flex justify-end items-center gap-4 ${isSendEmailLoading ? 'opacity-50 pointer-events-none animate-pulse' : ''}`">
      <vs-button
        class="button font-semibold h-8 p-0"
        color="primary"
        type="border"
        :disabled="isSendEmailLoading"
        @click="$emit('close')"
      >
        Cancel
      </vs-button>
      <vs-button
        class="button font-semibold h-8 p-0"
        @click="sendEmail"
        :disabled="isSendEmailLoading"
      >
        Send
      </vs-button>
    </div>
  </vs-popup>
</template>

<script>
import moment from 'moment'

export default {
  name: 'SendEmailModal',
  props: {
    filters: {
      type: Object,
      default: () => ({company: '', month_year: ''})
    },
    isModalShown: {
      type: Boolean,
      default: () => false
    }
  },
  watch: {
    fuelSupplyType: {
      immediate: true,
      async handler(val) {
        await this.toggleEmailReport(
          val,
          'fuelSupply',
          'dailyBunker',
          'fuel-supply',
          'fuelSupplyAttachment'
        )
      }
    },
    dailyBunkerType: {
      immediate: true,
      async handler(val) {
        await  this.toggleEmailReport(
          val,
          'dailyBunker',
          'fuelSupply',
          'daily-bunker',
          'dailyBunkerAttachment'
        )
      }
    },
    fuelSupplyAttachment: {
      immediate: true,
      handler(val) {
        if(val) {
          this.fuelSupplyType = val;
        }

      }
    },
    dailyBunkerAttachment: {
      immediate: true,
      handler(val) {
        if(val) {
          this.dailyBunkerType = val;
        }
      }
    },
    isModalShown: {
      immediate: true,
      async handler(val) {
        if (!val) {
          this.emails = ''
          this.fuelSupplyType = false
          this.dailyBunkerType = false
          this.fuelSupplyAttachment = false
          this.dailyBunkerAttachment = false
          this.defaultRecipients = {
            fuelSupply: [],
            dailyBunker: []
          }
        }
      }
    }
  },
  data() {
    return {
      emails: '',
      fuelSupplyType: false,
      dailyBunkerType: false,
      fuelSupplyAttachment: false,
      dailyBunkerAttachment: false,
      isSendEmailLoading: false,
      defaultRecipients: {
        fuelSupply: [],
        dailyBunker: []
      }
    }
  },
  computed: {
    populateTypes() {
      const types = []
      if(this.fuelSupplyType) types.push('FUEL_SUPPLY_REPORT')
      if(this.dailyBunkerType) types.push('DAILY_BUNKER_REPORT')
      return types
    },
    populateHasAttachments() {
      const hasAttachments = []
      if(this.fuelSupplyAttachment) hasAttachments.push('FUEL_SUPPLY_REPORT')
      if(this.dailyBunkerAttachment) hasAttachments.push('DAILY_BUNKER_REPORT')
      return hasAttachments
    }
  },
  methods: {
    async toggleEmailReport(isChecked, currentKey, otherKey, apiType, attachmentKey) {
      const currentEmails = this.emails
        .split(',')
        .map(email => email.trim())
        .filter(email => email)

      if (isChecked) {
        const newDefaults = await this.$store.dispatch(
          'FuelSupplyManagement/getDefaultEmailRecipients',
          { type: apiType }
        )
        this.defaultRecipients[currentKey] = newDefaults
        this.emails = [...new Set([...currentEmails, ...newDefaults])].join(
          ', '
        )
      } else {
        const otherDefaults = this.defaultRecipients[otherKey] || []
        const emailsToRemove = this.defaultRecipients[currentKey].filter(
          email => !otherDefaults.includes(email)
        )

        const filteredEmails = currentEmails.filter(
          email => !emailsToRemove.includes(email)
        )

        this.emails = filteredEmails.join(', ')
        this[attachmentKey] = false
        this.defaultRecipients[currentKey] = []
      }
    },
    insertComma() {
      const value = this.emails.trim();
      if (value.endsWith(",")) {
        this.emails = `${value} `;
        return;
      }
      this.emails = `${value}, `;
    },
    emailValidation() {
      if(this.emails.length > 0) {
        const list = this.emails
          .split(",")
          .map(e => e.trim())
          .filter(e => e);

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        const errors = []
        list.forEach(email => {
          if(!emailRegex.test(email))
            errors.push(email)
        })
        if(errors.length) {
          this.showFailedNotification(this, `${errors[0]} is not a valid email format. Please enter a valid email address.`)
        }
        return !errors.length
      }
      return false
    },
    async sendEmail() {
      if(this.emailValidation()) {
        try {
          this.isSendEmailLoading = true
          await this.$store.dispatch('FuelSupplyManagement/sendFuelSupplyEmail', {
            recipient_emails: this.emails.split(',').map(e => e.trim()),
            types: this.populateTypes,
            month_year: moment(this.filters.month_year).format('MM-YYYY'),
            company: this.filters?.company?.length ? this.filters.company.map(c => c.value).join(',') : null,
            has_attachment: this.populateHasAttachments
          })
          this.showSuccessNotification(this, 'Fuel Supply Email successfully sent.')
          this.isSendEmailLoading = false
          this.$emit('close')
        } catch(e) {
          this.isSendEmailLoading = false
          this.showFailedNotification(this, e?.data?.error?.message ||'Failed to send email, please try again.')
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.popup {
  /deep/.vs-popup {
    @apply w-1/3;
    @apply rounded-2xl;

    &--title {
      @apply hidden;
    }

    &--content {
      @apply relative;
      @apply p-8;
      @apply m-0;
      @apply flex;
      @apply flex-col;
      @apply h-full;

      @apply items-center;
      @apply text-center;
    }

    &--close-button {
      @apply cursor-pointer;
      @apply absolute;
      width: 20px;
      top: 1rem;
      right: 1rem;
    }
  }
  /deep/.vs-textarea {
    @apply h-full;
  }
  /deep/.vs-checkbox--check {
    transform: none !important;
  }
}
</style>
